<template>
    <!-- Базовый контейнер для страниц -->
    <MainLayout>

        <!-- Блок поиска -->
        <div class="search-container">
            <input
                v-model="searchQuery"
                @input="handleSearchInput"
                placeholder="Введите Telegram ID, имя или username..."
                type="text"
                class="search-input"
                :disabled="loading"
            />
            <div v-if="searchQuery" @click="clearSearch" class="search-clear-btn" title="Очистить поиск">
                ×
            </div>
        </div>

        <!-- Контейнер всех дроп пользователй -->
        <div class="all-drop-profiles-conatiner" ref="container" @scroll="handleScroll">

            <!-- Карточки профилей дроп-пользователей -->
            <div v-for="profile in profiles" :key="profile.id" class="drop-user-card-block">
                <img 
                    :src="profile.profile_photo || require('@/assets/images-templates/zbs-drop-no-photo.jpg')" 
                    alt="Фото профиля дроп-пользователя" 
                    class="drop-user-image"
                    @error="handleImageError"
                >

                <!-- Основная информация о дроп пользователе -->
                <div class="drop-user-info-block">

                    <!-- Линия данных пользваоетеля (имя)-->
                    <div class="drop-user-data-line">
                        <div class="drop-user-data-label">Имя:</div>
                        <div class="drop-user-data-value text-wrap">{{ profile.first_name || 'Не указано' }}</div>
                    </div>

                    <!-- Линия данных пользваоетеля (username)-->
                    <div class="drop-user-data-line">
                        <div class="drop-user-data-label">Username:</div>
                        <div class="drop-user-data-value text-wrap">{{ profile.username ? '@' + profile.username : 'Не указан' }}</div>
                    </div>

                    <!-- Линия данных пользваоетеля (ID)-->
                    <div class="drop-user-data-line">
                        <div class="drop-user-data-label">Telegram ID:</div>
                        <div class="drop-user-data-value text-wrap">{{ profile.user_id }}</div>
                    </div>
                </div>

                <!-- Кнопка перехода к странице управления -->
                <div 
                    class="drop-control-btn"
                    @click="goToDropProfile(profile.user_id)"
                >
                    Управлять
                </div>
            </div>

            <!-- Индикатор загрузки -->
            <div v-if="loading" class="loading-indicator">
                {{ searchQuery ? 'Поиск...' : 'Загрузка...' }}
            </div>

            <!-- Сообщение об ошибке -->
            <div v-if="error" class="error-message">
                Ошибка: {{ error }}
            </div>

            <!-- Индикатор конца списка -->
            <div v-if="hasMorePages && !loading && !searchQuery" class="load-more-indicator">
                Прокрутите вниз для загрузки
            </div>
            <div v-else-if="profiles.length > 0 && !hasMorePages && !loading" class="end-of-list">
                {{ searchQuery ? 'Поиск завершен' : 'Все профили загружены' }}
            </div>

            <!-- Сообщение если нет профилей -->
            <div v-if="!loading && profiles.length === 0 && !error" class="no-profiles">
                {{ searchQuery ? `По запросу "${searchQuery}" ничего не найдено` : 'Дроп-пользователи не найдены' }}
            </div>

        </div>

    </MainLayout>
</template>

<script>
import MainLayout from '@/components/MainLayout.vue';
import api from '@/api/api';

export default {
    components: {
        MainLayout
    },
    data() {
        return {
            profiles: [],
            loading: false,
            error: null,
            currentPage: 1,
            pageSize: 20,
            totalPages: 0,
            totalCount: 0,
            hasMorePages: true,
            scrollDebounce: null,
            // Новые данные для поиска
            searchQuery: '',
            searchDebounce: null,
            isSearchActive: false
        };
    },
    created() {
        this.showBackButton();
    }, 
    mounted() {
        this.loadProfiles();
    },
    methods: {
        // Метод для перехода на страницу управления профилем
        goToDropProfile(userId) {
            console.log('Переход к управлению профилем дропа с ID:', userId);
            this.$router.push({
                name: 'DropProfileSettingsPage',
                params: { dropId: userId }
            });
        },

        // Показать кнопку назад в Telegram WebApp
        showBackButton() {
            if (window.Telegram && window.Telegram.WebApp) {
                const backButton = window.Telegram.WebApp.BackButton.show();

                backButton.onClick(() => {
                    window.Telegram.WebApp.MainButton.hide();
                    backButton.offClick();
                    backButton.hide();
                    // Всегда перенаправляем на страницу менеджерства
                    this.$router.push('/manager');
                });
            }
        },

        // Загрузка профилей дроп-пользователей с пагинацией
        async loadProfiles() {
            if (this.loading || (!this.hasMorePages && !this.isSearchActive)) return;
            
            this.loading = true;
            this.error = null;
            
            try {
                // Параметры запроса
                const params = {
                    page: this.currentPage,
                    page_size: this.pageSize
                };

                // Если есть поисковый запрос, добавляем его
                if (this.searchQuery && this.searchQuery.trim()) {
                    params.search = this.searchQuery.trim();
                }

                const response = await api.get('/drop/profile/profiles', { params });
                const data = response.data;
                
                // Если это новый поиск или первая загрузка, заменяем список
                if ((this.isSearchActive && this.currentPage === 1) || (this.currentPage === 1 && !this.isSearchActive)) {
                    this.profiles = data.profiles;
                } else {
                    // При пагинации добавляем к существующему
                    this.profiles = [...this.profiles, ...data.profiles];
                }
                
                // Обновляем метаданные пагинации
                this.totalCount = data.total_count;
                this.totalPages = data.total_pages;
                this.hasMorePages = this.currentPage < data.total_pages;
                
                // Увеличиваем номер страницы для следующей загрузки
                this.currentPage++;
                
            } catch (error) {
                console.error('Ошибка загрузки профилей:', error);
                this.error = error.response?.data?.detail || 'Неизвестная ошибка';
            } finally {
                this.loading = false;
            }
        },
        
        // Обработка ввода в поле поиска
        handleSearchInput() {
            // Отменяем предыдущий таймер
            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce);
            }
            
            // Устанавливаем новый таймер (дебаунс 500мс)
            this.searchDebounce = setTimeout(() => {
                this.performSearch();
            }, 1000);
        },
        
        // Выполнение поиска
        performSearch() {
            if (this.searchQuery.trim() === '') {
                // Если поиск очищен
                this.clearSearch();
                return;
            }

            // Сбрасываем состояние пагинации для нового поиска
            this.isSearchActive = true;
            this.currentPage = 1;
            this.profiles = [];
            this.hasMorePages = true;

            // Загружаем результаты поиска
            this.loadProfiles();
        },

        // Очистка поиска
        clearSearch() {
            this.searchQuery = '';
            this.isSearchActive = false;
            this.currentPage = 1;
            this.profiles = [];
            this.hasMorePages = true;

            // Загружаем исходные данные
            this.loadProfiles();
        },
        
        handleScroll(event) {
            // Отменяем предыдущий таймер
            if (this.scrollDebounce) {
                clearTimeout(this.scrollDebounce);
            }
            
            // Устанавливаем новый таймер для дебаунса
            this.scrollDebounce = setTimeout(() => {
                const container = event.target;
                const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                
                // Загружаем следующую страницу если до конца меньше 100px
                // Для поиска также поддерживаем пагинацию, если результаты не помещаются на одной странице
                if (scrollBottom < 100 && this.hasMorePages && !this.loading) {
                    this.loadProfiles();
                }
            }, 100);
        },
        
        handleImageError(event) {
            // Заменяем битое изображение на дефолтное
            event.target.src = require('@/assets/images-templates/zbs-drop-no-photo.jpg');
        }
    }
};
</script>

<style scoped>
/* Стили остаются без изменений */
.search-container {
    position: fixed;
    top: 25vw;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 10px;
    display: flex;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 25px;
    backdrop-filter: blur(10px);
}

.search-input {
    width: 100%;
    padding: 12px 40px 12px 15px;
    background-color: transparent !important;
    color: white;
    border: 2px solid #005ADA;
    border-radius: 15px;
    font-family: 'CentureGothic', sans-serif;
    font-size: 14px;
    outline: none;
    transition: all 0.3s ease;
}

.search-input:focus {
    border-color: #0BDA51;
    box-shadow: 0 0 0 3px rgba(11, 218, 81, 0.2);
}

.search-input::placeholder {
    color: #666;
}

.search-clear-btn {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    background-color: #666;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    user-select: none;
}

.search-clear-btn:hover {
    background-color: #005ADA;
    transform: translateY(-50%) scale(1.1);
}

.all-drop-profiles-conatiner {
    height: calc(100vh - 60px);
    overflow-y: auto;
    padding: 10px;
    position: fixed;
    left: 0;
    right: 0;
    top: 40vw;
    bottom: 0;
} 

.drop-user-card-block {
    font-family: 'CentureGothic', sans-serif;
    width: 100%;
    background-color: #1C1C1D;
    border-radius: 10px;
    height: 30vw;
    color: white;
    display: flex;
    padding: 2vw;
    gap: 2vw;
    margin-bottom: 3vw;
    position: relative;
}

.drop-user-data-line {
    display: flex;
    gap: 1vw;
    align-items: flex-start;
    font-size: 12px;
}

.drop-user-image {
    width: 26vw;
    height: 26vw;
    border-radius: 10px;
    object-fit: cover;
}

.drop-user-data-label {
    color: #005ADA;
    white-space: nowrap;
}

.text-wrap {
    color: white;
    word-break: break-word;
    overflow-wrap: break-word;
    flex: 1;
}

.drop-user-data-line:nth-child(2) .text-wrap {
    word-break: break-all;
}

.drop-user-data-value {
    color: white;
}

.drop-control-btn {
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: #005ADA;
    width: 25vw;
    height: 8vw;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    cursor: pointer;
    user-select: none;
    outline: none;
}

.drop-control-btn:hover {
    background-color: #0047B3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 90, 218, 0.3);
}

.drop-control-btn:active {
    background-color: #003D99;
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 90, 218, 0.2);
    transition: all 0.1s ease;
}

.drop-control-btn:focus {
    box-shadow: 0 0 0 3px rgba(0, 90, 218, 0.4);
}

.loading-indicator,
.load-more-indicator,
.end-of-list,
.no-profiles {
    text-align: center;
    padding: 20px;
    color: #005ADA;
    font-size: 16px;
    width: 100%;
}

.error-message {
    text-align: center;
    padding: 20px;
    color: #F44336;
    background-color: rgba(244, 67, 54, 0.1);
    border-radius: 10px;
    margin: 10px 0;
    width: 100%;
}
</style>